import { DateService, EventAdapterService } from '@pulse/browser-shared';
import { Experiment, PulseStorage, SettingsConfigurations } from '@pulse/common';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SettingsResponse, SettingsUsage } from '../models';
import { CacheService } from './cache.service';
import { ResponseTrackerService } from './response-tracker.service';
import { UsageTrackerService } from './usage-tracker.service';

export class ExperimentsService {
    private experiments: Experiment[] | null = [];
    private experimentsSubject$ = new ReplaySubject<Experiment[]>(1);

    constructor(
        private pulseStorage: PulseStorage,
        protected readonly cookieStorage: PulseStorage,
        private dateService: DateService,
        private responseTrackerService: ResponseTrackerService,
        private cacheService: CacheService,
        private usageTrackerService: UsageTrackerService,
        private eventAdapterService: EventAdapterService,
        private config: SettingsConfigurations,
    ) {
        //TODO: remove after 2 moths of the release
        this.cookieStorage.remove(this.config.experimentStorageKey);

        this.experiments = this.pulseStorage.getJSON<Experiment[]>(
            this.config.experimentStorageKey,
        );

        // Emit initial experiments if any exist
        if (this.experiments && this.experiments.length > 0) {
            this.experimentsSubject$.next(this.experiments);
        }

        this.responseTrackerService.responses().subscribe((responses) => {
            this.cacheResponse(responses);
        });

        this.usageTrackerService.usages().subscribe((usages: SettingsUsage) => {
            const unparticipatedExperiment = this.findUnparticipatedExperiment(usages.key);

            if (unparticipatedExperiment && this.experiments) {
                this.eventAdapterService.push({
                    pulse: 'event',
                    event_type: 'experiment_participate',
                    data: {
                        experiment_id: unparticipatedExperiment.name,
                        variant: unparticipatedExperiment.variant,
                    },
                });

                //TODO: should be removed later
                this.experiments = this.experiments.map((experiment) => {
                    const actual = experiment?.actual === undefined ? true : experiment?.actual;

                    return { ...experiment, ...{ actual } };
                });

                this.cacheExperiments(this.experiments);
            }
        });

        this.pulseStorage.change(this.config.experimentStorageKey).subscribe((change) => {
            if (change.value) {
                this.experiments = JSON.parse(change.value);
            } else {
                this.experiments = [];
            }

            // Emit the updated experiments
            this.experimentsSubject$.next(this.experiments || []);
        });
    }

    getExperiments() {
        return this.pulseStorage.getJSON<Experiment[]>(this.config.experimentStorageKey) || [];
    }

    /**
     * Get an observable that emits experiment changes.
     * Emits the current experiments array when subscribed and whenever experiments change.
     */
    getExperiments$(): Observable<Experiment[]> {
        return this.experimentsSubject$.asObservable();
    }

    getExperimentsString() {
        return this.getExperiments()
            .map((experiment) => `${experiment.name}:${experiment.variant}`)
            .join(',');
    }

    /**
     * Get an observable that emits experiments string changes.
     * Emits the current experiments string when subscribed and whenever experiments change.
     */
    getExperimentsString$(): Observable<string> {
        return this.experimentsSubject$
            .asObservable()
            .pipe(
                map((experiments) =>
                    experiments
                        .map((experiment) => `${experiment.name}:${experiment.variant}`)
                        .join(','),
                ),
            );
    }

    fireExperimentPopulation(experiments: Experiment[]) {
        if (experiments && experiments.length) {
            experiments.forEach((experiment) => {
                if (experiment.trackable) {
                    this.eventAdapterService.push({
                        pulse: 'event',
                        event_type: 'experiment_populate',
                        data: {
                            experiment_id: experiment.name,
                            variant: experiment.variant,
                        },
                    });
                }
            });
        }
    }

    cacheResponse(response: SettingsResponse) {
        if (!response.experiments) {
            return;
        }

        response.experiments = response.experiments.map((exp) => ({ ...exp, ...{ actual: true } }));
        const newExperiments = this.findNewExperiments(response.experiments);
        const experimentsDifferent = this.experimentDifferent(response.experiments);

        if (newExperiments && newExperiments.length) {
            this.cacheService.removeCachedSettings(newExperiments);
            this.cacheExperiments(response.experiments);
        }

        if (!(newExperiments && newExperiments.length) && experimentsDifferent) {
            this.cacheExperiments(response.experiments);
        }

        this.fireExperimentPopulation(newExperiments);
    }

    private cacheExperiments(experiments: Experiment[]) {
        const time = this.dateService.now();
        const cachedExperiments = this.getExperiments();
        const experimentsToStore = experiments.map((experiment) => {
            const matchedExperiment = cachedExperiments.find((ex) => ex.name === experiment.name);

            const participation_date =
                matchedExperiment?.participation_date || experiment?.participation_date || null;
            const participation_session =
                matchedExperiment?.participation_session ||
                experiment?.participation_session ||
                null;

            return { ...experiment, participation_date, participation_session, time };
        });

        // Add experiments from the cache that are not in the new list
        const missingExperiments = cachedExperiments
            .filter(
                (cachedExperiment) =>
                    !experiments.some(
                        (newExperiment) => newExperiment.name === cachedExperiment.name,
                    ),
            )
            .map((exp) => ({ ...exp, ...{ actual: false } }));

        const finalExperimentsToStore = [...experimentsToStore, ...missingExperiments];

        this.experiments = finalExperimentsToStore;

        this.pulseStorage.remove(this.config.experimentStorageKey);
        this.pulseStorage.set(
            this.config.experimentStorageKey,
            JSON.stringify(finalExperimentsToStore),
        );

        // Emit the updated experiments to reactive subscribers
        this.experimentsSubject$.next(finalExperimentsToStore);
    }

    findNewExperiments(experiments: Experiment[]): Experiment[] {
        const cachedExperiments = this.getExperiments();

        if (!cachedExperiments) {
            return experiments;
        }

        const newExperiments: Experiment[] = experiments.filter(
            (experiment) => cachedExperiments.findIndex((ex) => ex.name === experiment.name) === -1,
        );

        return newExperiments;
    }

    private experimentDifferent(newExperiments: Experiment[]): boolean {
        if (!this.experiments || !this.experiments.length) {
            return false;
        }
        for (const experiment of this.experiments) {
            if (
                newExperiments.findIndex(
                    (newExperiment) => newExperiment.name === experiment.name,
                ) === -1
            ) {
                return true;
            }
        }

        return false;
    }

    private findUnparticipatedExperiment(settingName: string): Experiment | null {
        if (!this.experiments || !this.experiments.length) {
            return null;
        }

        for (const experiment of this.experiments) {
            if (experiment.settings?.includes(settingName) && !experiment.participation_date) {
                experiment.participation_date = this.dateService.now();
                experiment.participation_session = this.pulseStorage.get(this.config.keySessionID);

                return experiment;
            }
        }

        return null;
    }
}
